import { RestError } from '@azure/core-http';
import {
  CarrierDto,
  EditCarrierDto,
  KnownCourierLoginMethod,
  KnownGroupRegistrationPolicy
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { useForm } from 'react-hook-form';
import { castDraft } from 'immer';
import { useImmer } from 'use-immer';
import { registrationPolicyToLabel } from '../../../../../ui/components/pure/registration-policy-badge/registration-policy-to-label.map';
import { useMutationEditCarrier } from '../../../carriers.queries';

export type EditPoliciesFormState = {
  kioskRegistrationPolicy: string;
  qrRegistrationPolicy: string;
  loginMethod: string;
};

/** Stands for "no method of its own", which the endpoint is told as null. */
export const LOGIN_METHOD_DEFAULT = 'default';

const loginMethodLabels: Record<string, string> = {
  [KnownCourierLoginMethod.Mobile]: 'Mobile Number',
  [KnownCourierLoginMethod.Id]: 'ID/External ID',
  [KnownCourierLoginMethod.AccessCode]: 'Access Code'
};

export const loginMethodOptions = [
  { label: 'Default', value: LOGIN_METHOD_DEFAULT },
  ...Object.values(KnownCourierLoginMethod).map((value) => ({ label: loginMethodLabels[value] ?? value, value }))
];

export interface EditPoliciesActionProps {
  carrier: CarrierDto;
  onAbort(): void;
  onSuccess(): void;
}

/** The same three values for both, labelled as the rest of the UI labels them. */
export const registrationPolicyOptions = Object.values(KnownGroupRegistrationPolicy).map((value) => ({
  label: registrationPolicyToLabel[value] ?? value,
  value
}));

export function useEditPoliciesAction({ carrier, onAbort, onSuccess }: EditPoliciesActionProps) {
  const [state, setState] = useImmer<{ saving: boolean; error?: RestError | Error }>({ saving: false });

  const {
    control,
    handleSubmit,
    formState: { dirtyFields }
  } = useForm<EditPoliciesFormState>({
    defaultValues: {
      kioskRegistrationPolicy: carrier.kioskRegistrationPolicy ?? KnownGroupRegistrationPolicy.Hidden,
      qrRegistrationPolicy: carrier.qrRegistrationPolicy ?? KnownGroupRegistrationPolicy.Hidden,
      loginMethod: carrier.loginMethod ?? LOGIN_METHOD_DEFAULT
    }
  });

  const editMutation = useMutationEditCarrier(carrier.id);

  const handlers = {
    onCancel: onAbort,
    onSubmit(form: EditPoliciesFormState) {
      setState((draft) => {
        draft.saving = true;
      });

      const dto: EditCarrierDto = {
        kioskRegistrationPolicy: form.kioskRegistrationPolicy,
        qrRegistrationPolicy: form.qrRegistrationPolicy
      };

      // A carrier reports an inherited method as its own, so writing back what was read would pin
      // it here. Only a deliberate change is sent; null is how the field is cleared.
      if (dirtyFields.loginMethod) {
        dto.loginMethod = form.loginMethod === LOGIN_METHOD_DEFAULT ? null : form.loginMethod;
      }

      editMutation.mutate(dto, {
        onSuccess() {
          onSuccess();
        },
        onError(error) {
          setState((draft) => {
            draft.saving = false;
            draft.error = castDraft(error);
          });
        }
      });
    }
  };

  return { state, handlers, control, handleSubmit } as const;
}
